import { Handshake, Zap, Heart, Search, MessageCircle } from "lucide-react";
import { Button } from "../ui/button";
import { openLiveChat } from "../../lib/chat";

const pillars = [
  {
    icon: Search,
    title: "All credit, all welcome.",
    text: "Strong credit? Thin file? Somewhere in between? We fund the full spectrum — and we never reduce you to a single number. Other lenders overlook good businesses; we look at your whole story.",
  },
  {
    icon: Zap,
    title: "Speed that respects you.",
    text: "Pre-qualifications in minutes. Funding as fast as 24 hours. Because you've got payroll on Friday.",
  },
  {
    icon: Heart,
    title: "Real humans. Real warmth.",
    text: "No call-center scripts. Your dedicated funding advisor knows your name, your business, and your goals.",
  },
  {
    icon: Handshake,
    title: "Flexible terms, personal plans.",
    text: "Personalized repayment plans built around your cash flow — not a rigid spreadsheet. Loans that fit your life.",
  },
];

export default function WhyUs() {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative py-24 md:py-32 bg-secondary/50"
    >
      <div className="container-tight grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
            Why OliveUp
          </span>
          <h2
            data-testid="why-us-headline"
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-foreground leading-[1.05] text-balance"
          >
            We say YES <br />
            <span className="text-primary">when others say no.</span>
          </h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            Got turned down somewhere else? Welcome home. OliveUp works with
            all credit types, dedicated humans, fast turnarounds, and flexible
            terms — so small businesses can grow without jumping through hoops
            built for someone else.
          </p>

          <div
            id="team"
            data-testid="team-image"
            className="group mt-10 relative aspect-[4/3] w-full overflow-hidden rounded-3xl border border-border bg-muted shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
          >
            <img
              src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=80"
              alt="Men and women collaborating at a long table in a bright, open-plan tech office with laptops—startup energy, approachable and real"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.02]"
            />
          </div>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            Think Google-campus meets Main Street: mixed team, big windows,
            laptops open, zero stiff suits. We&apos;re down-to-earth on purpose—so
            you never feel like you&apos;re pitching a panel that forgot what
            running a business actually feels like.
          </p>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {pillars.map((p, i) => (
            <div
              key={p.title}
              data-testid={`pillar-${i}`}
              className="bg-card border border-border rounded-3xl p-7 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(0,0,0,0.07)]"
            >
              <div className="h-11 w-11 rounded-xl bg-accent/15 flex items-center justify-center text-accent">
                <p.icon className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <h3 className="mt-6 font-display text-xl font-bold text-foreground">
                {p.title}
              </h3>
              <p className="mt-2 text-base text-muted-foreground leading-relaxed">
                {p.text}
              </p>
            </div>
          ))}
        </div>

        {/* Fills the visual band to the right of the team photo on large screens */}
        <div
          className="col-span-full lg:col-span-7 lg:col-start-6 mt-2 lg:mt-4"
          data-testid="why-us-fill-panel"
        >
          <div className="rounded-3xl border border-border bg-card/90 p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
            <p className="font-display text-lg md:text-xl font-bold text-foreground">
              Real humans, flexible terms—here&apos;s what that looks like day to day
            </p>
            <ul className="mt-4 space-y-3 text-sm md:text-base text-muted-foreground leading-relaxed">
              <li className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                A named advisor who texts like a human, not a ticket queue.
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                Plain-language term sheets—no hiding the important numbers on page nine.
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                Room to pivot if your cash flow changes—we build plans around reality, not fantasy.
              </li>
            </ul>
            <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
              <Button
                asChild
                className="rounded-full bg-accent hover:bg-[#D1664A] text-accent-foreground font-semibold"
              >
                <a href="#apply" data-testid="why-us-fill-apply">
                  Start an application
                </a>
              </Button>
              <Button
                type="button"
                variant="outline"
                className="rounded-full border-primary/30 font-semibold text-primary hover:bg-primary/5"
                onClick={openLiveChat}
                data-testid="why-us-fill-chat"
              >
                <MessageCircle className="h-4 w-4" />
                Chat with us first
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
